/**
 * FAB reference visual configuration — toggled via URL search params in dev.
 *
 * ?referenceMatch=1  — fixed camera/lighting/exposure for screenshot comparison
 * ?referenceGeometry=1 — neutral gray silhouette mode (Part 59)
 * ?referenceMaterial=1 — neutral materials, no post (Part 60)
 * ?referenceGlass=1   — glass close-up debug (Part 61)
 * ?debugMaterial=neutral|wireframe|material|glass|lighting
 * ?noPost=1           — disable postprocessing
 * ?sharp=1            — disable post FX for maximum sharpness
 */

export type DebugMaterialMode = 'neutral' | 'wireframe' | 'material' | 'glass' | 'lighting';

export type FabVisualConfig = {
  referenceMatchMode: boolean;
  referenceGeometryMode: boolean;
  referenceMaterialMode: boolean;
  referenceGlassMode: boolean;
  debugMaterialMode: DebugMaterialMode | null;
  toneMappingExposure: number;
  enablePostProcessing: boolean;
  enableShadows: boolean;
  environmentIntensity: number;
  /** Full procedural kits for every machine (perf LOD deferred). */
  equipmentDetailLimit: number;
  equipmentNearPx: number;
};

const DEBUG_MATERIAL_VALUES = new Set<DebugMaterialMode>([
  'neutral',
  'wireframe',
  'material',
  'glass',
  'lighting',
]);

function readSearchParam(key: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(key);
}

export function getFabVisualConfig(): FabVisualConfig {
  const referenceMatchMode = readSearchParam('referenceMatch') === '1';
  const referenceGeometryMode = readSearchParam('referenceGeometry') === '1';
  const referenceMaterialMode = readSearchParam('referenceMaterial') === '1';
  const referenceGlassMode = readSearchParam('referenceGlass') === '1';
  const debugRaw = readSearchParam('debugMaterial');
  const debugMaterialMode =
    debugRaw && DEBUG_MATERIAL_VALUES.has(debugRaw as DebugMaterialMode)
      ? (debugRaw as DebugMaterialMode)
      : referenceGeometryMode
        ? 'neutral'
        : referenceMaterialMode
          ? 'material'
          : referenceGlassMode
            ? 'glass'
            : null;

  const disablePost =
    readSearchParam('noPost') === '1' ||
    debugMaterialMode === 'neutral' ||
    debugMaterialMode === 'material' ||
    debugMaterialMode === 'lighting';

  return {
    referenceMatchMode,
    referenceGeometryMode,
    referenceMaterialMode,
    referenceGlassMode,
    debugMaterialMode,
    toneMappingExposure: referenceMatchMode ? 0.96 : 0.94,
    enablePostProcessing: !disablePost && readSearchParam('sharp') !== '1',
    enableShadows: true,
    environmentIntensity: referenceMatchMode ? 1.05 : 1.0,
    equipmentDetailLimit: Number.POSITIVE_INFINITY,
    equipmentNearPx: referenceMatchMode ? 56 : 72,
  };
}

/** Reference camera presets (Part 41). */
export const REFERENCE_CAMERA_PRESETS = {
  REFERENCE_VIEW: {
    id: 'reference',
    label: 'Reference View',
    fov: 42,
    /** Front + ~30° side — matches reference hero angle. */
    offset: [1.05, 0.42, 0.88] as const,
    distanceScale: 1.15,
  },
  FRONT: { id: 'front', label: 'Front', fov: 40, offset: [0, 0.35, 1.2] as const, distanceScale: 1.1 },
  FRONT_45: {
    id: 'front45',
    label: 'Front 45°',
    fov: 42,
    offset: [0.85, 0.38, 0.85] as const,
    distanceScale: 1.12,
  },
  RIGHT: { id: 'right', label: 'Right', fov: 40, offset: [1.2, 0.35, 0] as const, distanceScale: 1.1 },
  LEFT: { id: 'left', label: 'Left', fov: 40, offset: [-1.2, 0.35, 0] as const, distanceScale: 1.1 },
  BACK: { id: 'back', label: 'Back', fov: 40, offset: [0, 0.35, -1.2] as const, distanceScale: 1.1 },
  WIDE_FAB: { id: 'wide', label: 'Wide FAB', fov: 48, offset: [0.6, 0.55, 0.6] as const, distanceScale: 1.65 },
} as const;

export type ReferenceCameraPresetId = keyof typeof REFERENCE_CAMERA_PRESETS;

export function resolveReferenceCameraOffset(presetId: string): (typeof REFERENCE_CAMERA_PRESETS)[ReferenceCameraPresetId] | null {
  const entry = Object.values(REFERENCE_CAMERA_PRESETS).find((p) => p.id === presetId);
  return entry ?? null;
}
